import { useState } from 'react'
import { api } from './api'
import { useAbruf, useHintergrundabruf } from './Abruf'
import { fachlicherSatz } from './Fehlersatz'
import { Periodenfeld } from './Periodenfeld'
import { eingefrorene } from './Positionskonten'
import { useDarf } from './Rechte'
import { kontobezeichnung, t } from './sprache'
import { Blattabschnitt, periodenwort } from './Stammdaten'
import type { Abschluss } from './typen'

const KEINE_ABSCHLUESSE: Abschluss[] = []

/**
 * **Der erste offene Monat** (R1657): der nach dem letzten freigegebenen,
 * sonst die Periode des Berichts (Entscheidung des Auftraggebers vom
 * 2026-10-12). Abgeschlossene Monate bleiben, wie sie sind.
 */
export function ersterOffenerMonat(abschluesse: Abschluss[], periode?: number): number | null {
  const gefroren = eingefrorene(abschluesse)
  const letzter = gefroren[gefroren.length - 1]
  if (letzter === undefined) return periode ?? null
  return letzter % 100 === 12 ? (Math.floor(letzter / 100) + 1) * 100 + 1 : letzter + 1
}

/**
 * **Ausweis der Stichtagskursanpassung auf separatem Konto** — im Kontoblatt
 * (2.6 Abschnitt 2.14, R1655–R1659).
 *
 * Der Haken steht nur an einem Konto, dessen Umrechnung vom Stichtagskurs
 * abweicht — zum historischen Kurs oder am Jahresergebnis (R1655). Gesetzt,
 * legt CoKo unter A.IV das eigene Konto an, das wie das Quellkonto heißt, mit
 * der Endung „– Stichtagskursanpassung“ (R1656, R1659). Er gilt ab einem
 * Monat, vorbelegt mit dem ersten offenen (R1657).
 */
export function Stichtagskursanpassung({
  konto,
  periode,
  aufAenderung,
}: {
  konto: string
  /** Die Periode des Berichts — der Vorschlag, wo noch nichts freigegeben ist. */
  periode?: number
  aufAenderung?: () => void
}) {
  const abruf = useAbruf(() => api.stichtagskursanpassung(konto), [konto])
  // Nur der Vorschlag für „ab“ hängt daran — im Hintergrund (R1007).
  const abschluesse = useHintergrundabruf(() => api.abschluesse(), [], KEINE_ABSCHLUESSE)
  const darf = useDarf()
  const [ab, setAb] = useState<number | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  const stand = abruf.wert
  // Wo der Haken nicht wirkt, steht er nicht (R1655, R293).
  if (!stand || !stand.anbieten) return null

  const darfSetzen = darf('stichtagskursanpassungSetzen')
  const monat = ab ?? ersterOffenerMonat(abschluesse, periode)

  async function setze(getrennt: boolean) {
    if (monat === null) {
      setMeldung(t('stichtagskursanpassung.ohnemonat'))
      return
    }
    setLaeuft(true)
    setMeldung(null)
    try {
      abruf.setze(await api.stichtagskursanpassungSetzen(konto, getrennt, monat))
      aufAenderung?.()
    } catch (fehler) {
      // Nur die fachliche Absage steht hier; eine Störung meldet das
      // Störungsfenster (7.2 Abschnitt 30, R1003/R1004).
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Blattabschnitt kennung="eigenschaften.stichtagskursanpassung" titel={t('stichtagskursanpassung.titel')}>
      {/* Der Text steht einmal, am Haken — bis zum 2026-10-12 stand er
          daneben noch als Überschrift der Gruppe. */}
      <div className="wirkungswahl">
        <label>
          <input
            type="checkbox"
            checked={stand.getrennt}
            disabled={!darfSetzen || laeuft}
            onChange={(e) => void setze(e.target.checked)}
          />
          {t('stichtagskursanpassung.haken')}
        </label>
        <Periodenfeld
          wert={monat}
          aufWert={setAb}
          gesperrt={!darfSetzen || laeuft}
          beschriftung={t('stichtagskursanpassung.ab')}
          klasse=""
        />
      </div>
      {/* Seit wann, und wohin — das eigene Konto mit seinem Namen (R1659). */}
      {stand.ab !== null && (
        <p className="erlaeuterung">
          {stand.getrennt && stand.ziel
            ? t('stichtagskursanpassung.gilt', { ab: periodenwort(stand.ab), konto: kontobezeichnung(stand.ziel) })
            : t('stichtagskursanpassung.giltnicht', { ab: periodenwort(stand.ab), konto: kontobezeichnung('KTO-2250') })}
        </p>
      )}
      {meldung && <p className="hinweis">{meldung}</p>}
    </Blattabschnitt>
  )
}
